import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { categoryEmoji } from '../categoryEmoji';
import { getCachedData, refreshCache } from '../store';
import { todayISO, toISODate, formatDate, formatMoneyPrecise } from '../dateUtils';
import { plannedTransfersNet, plannedDebtReturnsNet, plannedOperations, isIncludedAccount, totalBalance, effectivePlanned, dailyBudgetModel } from '../balance';
import PeriodForm from './PeriodForm';
import Modal from './Modal';
import TransactionForm from './TransactionForm';
import Spinner from './Spinner';

export default function Dashboard({ user }) {
  const [periods, setPeriods] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // Просматриваемая дата (навигация вперёд/назад на главной)
  const [viewDate, setViewDate] = useState(() => todayISO());
  const [availableTotal, setAvailableTotal] = useState(0);
  const [todayRemaining, setTodayRemaining] = useState(0);
  const [dailyBudget, setDailyBudget] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [periodEditing, setPeriodEditing] = useState(null);
  // Запланированная операция, которую пользователь подтверждает
  const [confirmPlanned, setConfirmPlanned] = useState(null);

  // Активный период — тот, в чей диапазон попадает просматриваемая дата
  const activePeriod = periods.find(p => viewDate >= p.startDate && viewDate <= p.endDate) || null;

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : 'Без категории';
  };

  const getCategoryIcon = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return categoryEmoji(cat ? cat.name : '');
  };

  const loadData = useCallback(async () => {
    try {
      const [periodsData, cats, accs, tx] = await Promise.all([
        api.getPeriods(),
        api.getCategories(),
        api.getAccounts(),
        api.getTransactions(),
      ]);
      setCategories(cats);
      setAccounts(accs);
      setTransactions(tx);
      setPeriods(periodsData);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const cached = getCachedData();
    if (cached && Array.isArray(cached.categories)) setCategories(cached.categories);
    loadData();
  }, [loadData]);

  // Пересчитываем суточный остаток сразу после добавления/изменения операции
  useEffect(() => {
    const onDataChanged = () => loadData();
    window.addEventListener('data:changed', onDataChanged);
    return () => window.removeEventListener('data:changed', onDataChanged);
  }, [loadData]);

  useEffect(() => {
    if (activePeriod) {
      // Динамическая модель: фиксированный базовый дневной лимит + накопительный перенос.
      // «Доступно сегодня» = база + перенос с прошлых дней + отклонения сегодня (траты сверх
      // плана вычитаются, доходы сверх плана добавляются). Недотраченное переносится на
      // следующий день, перерасход уходит в минус и компенсируется следующими днями.
      const model = dailyBudgetModel(activePeriod, categories, accounts, transactions, viewDate);
      if (model) {
        setAvailableTotal(model.periodBudget);
        setDailyBudget(model.baseDailyLimit);
        setTodayRemaining(model.availableToday);
      } else {
        setTodayRemaining(0);
        setAvailableTotal(0);
        setDailyBudget(0);
      }
    } else {
      setTodayRemaining(0);
      setAvailableTotal(0);
      setDailyBudget(0);
    }
  }, [activePeriod, viewDate, transactions, accounts, categories]);

  const handlePeriodSave = async (data) => {
    try {
      if (activePeriod) {
        await api.updatePeriod(activePeriod.id, data);
      } else {
        await api.createPeriod(data);
      }
      refreshCache();
    } catch (err) {
      console.error(err);
    }
    setShowPeriodForm(false);
    setPeriodEditing(null);
    setIsLoading(true);
    loadData();
  };

  if (isLoading) return (
    <div className="content">
      <Spinner label="Загрузка данных..." />
    </div>
  );

  const today = todayISO();
  const isToday = viewDate === today;
  // Все операции за выбранный день (только со счетов, входящих в общий баланс),
  // самые новые сверху. Доходы, расходы, переводы и долги в одном списке —
  // отличаются только цветом и иконкой.
  const viewTxs = transactions
    .filter(t => t.date === viewDate && ['income', 'expense', 'transfer', 'debt'].includes(t.type) && isIncludedAccount(accounts, t.accountId))
    .sort((a, b) => b.id - a.id);
  const viewIncomeSum = viewTxs.filter(t => t.type === 'income').reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const viewExpenseSum = viewTxs.filter(t => t.type === 'expense' || (t.type === 'debt' && (t.debtKind === 'return' || t.debtKind === 'lend')) || (t.type === 'transfer' && isIncludedAccount(accounts, t.accountId) && !isIncludedAccount(accounts, t.toAccountId))).reduce((s, t) => s + (Number(t.amount) || 0), 0);

  // Запланированные на сегодня операции из плана периода (с указанной датой платежа)
  const plannedTransfers = (activePeriod?.plannedTransfers || []);
  const plannedDebtReturns = (activePeriod?.plannedDebtReturns || []);

  const accountName = (id) => {
    const a = accounts.find(x => String(x.id) === String(id));
    return a ? a.name : '';
  };
  const debtName = (id) => {
    const a = accounts.find(x => String(x.id) === String(id));
    return a ? (a.person || a.name) : '';
  };

  // Уже проведённые (done) операции и операции завершённых категорий (finished)
  // не показываем, чтобы не провести их повторно
  const scheduledForToday = [
    ...plannedOperations(activePeriod?.plannedIncomes, 'income').filter(p => p.date === viewDate && !p.done && !p.finished),
    ...plannedOperations(activePeriod?.plannedExpenses, 'expense').filter(p => p.date === viewDate && !p.done && !p.finished),
    ...plannedTransfers.filter(p => p.date === viewDate && !p.done).map(p => ({ ...p, kind: 'transfer' })),
    ...plannedDebtReturns.filter(p => p.date === viewDate && !p.done).map(p => ({ ...p, kind: 'debtReturn' })),
  ];

  const getCategoryIdByName = (name) => {
    const cat = categories.find(c => c.name === name);
    return cat ? cat.id : null;
  };

  // Сдвиг просматриваемой даты
  const shiftViewDate = (delta) => {
    setViewDate(prev => {
      const d = new Date(prev + 'T00:00:00');
      d.setDate(d.getDate() + delta);
      return toISODate(d);
    });
  };

  // Открыть подтверждение запланированной операции
  const openConfirmPlanned = (item) => {
    setConfirmPlanned(item);
  };

  // Подтвердить — создать реальную транзакцию из запланированной
  const handleConfirmPlanned = async (data) => {
    try {
      await api.createTransaction(data);
      // Отметить запланированную операцию как выполненную в периоде
      const item = confirmPlanned;
      const kind = item && item.kind;
      if (activePeriod) {
        if (kind === 'income' || kind === 'expense') {
          const opId = item.id;
          const field = kind === 'income' ? 'plannedIncomes' : 'plannedExpenses';
          if (opId) {
            const groups = (activePeriod[field] || []).map(g => ({
              ...g,
              operations: (g.operations || []).map(o => (String(o.id) === String(opId) ? { ...o, done: true } : o)),
            }));
            await api.updatePeriod(activePeriod.id, { [field]: groups });
          }
        } else if (kind === 'transfer') {
          // Отметить плановый перевод выполненным (по паре счетов, сумме и дате)
          const transfers = (activePeriod.plannedTransfers || []).map(x =>
            String(x.fromAccountId) === String(item.fromAccountId) &&
            String(x.toAccountId) === String(item.toAccountId) &&
            Number(x.amount) === Number(item.amount) &&
            (x.date || '') === (item.date || '')
              ? { ...x, done: true } : x);
          await api.updatePeriod(activePeriod.id, { plannedTransfers: transfers });
        } else if (kind === 'debtReturn') {
          // Отметить плановый возврат долга выполненным
          const debts = (activePeriod.plannedDebtReturns || []).map(x =>
            String(x.accountId) === String(item.accountId) &&
            String(x.debtAccountId) === String(item.debtAccountId) &&
            Number(x.amount) === Number(item.amount) &&
            (x.date || '') === (item.date || '')
              ? { ...x, done: true } : x);
          await api.updatePeriod(activePeriod.id, { plannedDebtReturns: debts });
        }
      }
      await refreshCache();
    } catch (err) {
      console.error(err);
    }
    setConfirmPlanned(null);
    setIsLoading(true);
    loadData();
  };

  // Данные для формы подтверждения в зависимости от типа плана
  const plannedTx = confirmPlanned ? {
    ...(confirmPlanned.kind === 'transfer' ? {
      type: 'transfer',
      accountId: confirmPlanned.fromAccountId,
      toAccountId: confirmPlanned.toAccountId,
      amount: confirmPlanned.amount,
      date: confirmPlanned.date || viewDate,
      description: 'Перевод между счетами',
    } : confirmPlanned.kind === 'debtReturn' ? {
      type: 'debt',
      debtKind: 'return',
      accountId: confirmPlanned.accountId,
      debtAccountId: confirmPlanned.debtAccountId,
      amount: confirmPlanned.amount,
      date: confirmPlanned.date || viewDate,
      description: 'Возврат долга',
    } : {
      type: confirmPlanned.kind || confirmPlanned.type,
      categoryId: getCategoryIdByName(confirmPlanned.category),
      amount: confirmPlanned.amount,
      date: confirmPlanned.date || viewDate,
      description: confirmPlanned.comment || confirmPlanned.category || '',
    }),
  } : null;

  // Подпись для запланированной строки
  const scheduledTitle = (item) => {
    if (item.kind === 'transfer') return `Перевод: ${accountName(item.fromAccountId)} → ${accountName(item.toAccountId)}`;
    if (item.kind === 'debtReturn') return `Возврат долга: ${debtName(item.debtAccountId)}`;
    const base = item.category || (item.type === 'income' ? 'Доход' : 'Расход');
    return item.comment ? `${base} — ${item.comment}` : base;
  };
  const scheduledIcon = (item) => {
    if (item.kind === 'transfer') return '↔';
    if (item.kind === 'debtReturn') return '🤝';
    return categoryEmoji(item.category || '');
  };

  const actions = [
    { to: '/transactions', label: 'Траты', icon: '💳', cls: 'green' },
    { to: '/accounts', label: 'Счета', icon: '🏦', cls: 'blue' },
    { to: '/templates', label: 'Шаблоны', icon: '⚡', cls: 'orange' },
    { to: '/categories', label: 'Категории', icon: '🏷️', cls: 'pink' },
  ];

  return (
    <div className="content">
      <div className="greeting">
        <h1>С возвращением, {user?.username || 'друг'}! 👋</h1>
        <div className="greeting-date">Сегодня {formatDate(today)}</div>
      </div>

      <div className="date-nav">
        <button type="button" className="date-nav-btn" onClick={() => shiftViewDate(-1)} aria-label="Предыдущий день">◀</button>
        <button type="button" className="date-nav-label" onClick={() => setViewDate(today)} title="Вернуться к сегодня">
          {formatDate(viewDate)}{isToday ? ' · сегодня' : ''}
        </button>
        <button type="button" className="date-nav-btn" onClick={() => shiftViewDate(1)} aria-label="Следующий день">▶</button>
      </div>

      {showPeriodForm ? (
        <PeriodForm
          period={periodEditing}
          categories={categories}
          accounts={accounts}
          transactions={transactions}
          onSave={handlePeriodSave}
          onCancel={() => { setShowPeriodForm(false); setPeriodEditing(null); }}
        />
      ) : activePeriod ? (
        <div className="gradient-card">
          <div className="gradient-card-top">
            <div className="card-title">Период с {formatDate(activePeriod.startDate)} по {formatDate(activePeriod.endDate)}</div>
            <Link
              to="/periods"
              className="card-edit-btn"
              title="Управление периодами"
              aria-label="Управление периодами"
            >✏️</Link>
          </div>
          <div className="big-label">Доступно {formatDate(viewDate)}</div>
          <div key={`avail-${formatMoneyPrecise(todayRemaining)}`} className={`big-number${todayRemaining < 0 ? ' negative' : ''} animate-change`}>
            {formatMoneyPrecise(todayRemaining)} {todayRemaining < 0 ? '⚠️' : ''}
          </div>
          <div className="period-meta">
            <span>Суточный лимит: {formatMoneyPrecise(dailyBudget)} /день</span>
            <span>Всего доступно: {formatMoneyPrecise(availableTotal)}</span>
            <span>Доходы сегодня: {formatMoneyPrecise(viewIncomeSum)}</span>
            <span>Расходы сегодня: {formatMoneyPrecise(viewExpenseSum)}</span>
            <span>Дней осталось: {Math.max(0, Math.ceil((new Date(activePeriod.endDate + 'T00:00:00') - new Date(viewDate + 'T00:00:00')) / (1000 * 60 * 60 * 24)))}</span>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-title">Не запланированный период</div>
            <p style={{ margin: 0 }}>Выбранная дата не входит ни в один период. Запланируйте период, чтобы вести учёт.</p>
          <Link to="/periods" className="btn-link" style={{ display: 'inline-block', background: 'var(--primary)', color: '#04150b', padding: '11px 16px', borderRadius: 12, fontWeight: 700, textDecoration: 'none', marginTop: 10 }}>
            Запланировать период
          </Link>
        </div>
      )}

      <div className="action-grid">
        {actions.map(a => (
          <Link key={a.to} to={a.to} className="action-btn">
            <span className={`action-icon ${a.cls}`}>{a.icon}</span>
            <span>{a.label}</span>
          </Link>
        ))}
      </div>

      {scheduledForToday.length > 0 && (
        <>
          <h2 className="section-title">Запланировано на {formatDate(viewDate)}</h2>
          <ul className="list">
            {scheduledForToday.map((item, idx) => {
              const schedType = (item.kind === 'transfer' || item.kind === 'debtReturn') ? null : (item.type || item.kind);
              const schedCls = schedType === 'income' ? ' scheduled-income' : schedType === 'expense' ? ' scheduled-expense' : '';
              return (
                <li key={idx} className={`list-item scheduled-item${schedCls}`} onClick={() => openConfirmPlanned(item)}>
                  <div className="list-item-left">
                    <span className="cat-icon">{scheduledIcon(item)}</span>
                    <div>
                      <div className="list-item-title">{scheduledTitle(item)}</div>
                      <div className="list-item-sub">
                        {formatDate(item.date)} · нажмите, чтобы провести
                      </div>
                    </div>
                  </div>
                  <span className={`amount ${schedType === 'income' ? 'amount-income' : 'amount-expense'}`}>
                    {schedType === 'income' ? '+' : '−'}{formatMoneyPrecise(item.amount)}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <h2 className="section-title">Операции {formatDate(viewDate)}</h2>
      {viewTxs.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-dim)', textAlign: 'center' }}>
          {isToday ? 'Сегодня операций не было' : 'Операций в этот день не было'}
        </div>
      ) : (
        <ul className="list">
          {viewTxs.map(tx => {
            if (tx.type === 'transfer') {
              const out = isIncludedAccount(accounts, tx.accountId) && !isIncludedAccount(accounts, tx.toAccountId);
              return (
                <li key={tx.id} className="list-item">
                  <div className="list-item-left">
                    <span className="cat-icon">↔</span>
                    <div>
                      <div className="list-item-title">Перевод: {accountName(tx.accountId)} → {accountName(tx.toAccountId)}</div>
                      {tx.description && <div className="list-item-sub">{tx.description}</div>}
                    </div>
                  </div>
                  <span className={`amount ${out ? 'amount-expense' : 'amount-income'}`}>
                    {out ? '−' : '+'}{formatMoneyPrecise(tx.amount)}
                  </span>
                </li>
              );
            }
            if (tx.type === 'debt') {
              const isIn = tx.debtKind === 'borrow' || tx.debtKind === 'collect';
              const labels = { lend: 'Дал в долг', borrow: 'Взял в долг', return: 'Вернул долг', collect: 'Получил обратно' };
              return (
                <li key={tx.id} className="list-item">
                  <div className="list-item-left">
                    <span className="cat-icon">🤝</span>
                    <div>
                      <div className="list-item-title">{labels[tx.debtKind] || 'Долг'}: {debtName(tx.debtAccountId)}</div>
                      {tx.description && <div className="list-item-sub">{tx.description}</div>}
                    </div>
                  </div>
                  <span className={`amount ${isIn ? 'amount-income' : 'amount-expense'}`}>
                    {isIn ? '+' : '−'}{formatMoneyPrecise(tx.amount)}
                  </span>
                </li>
              );
            }
            const isIncome = tx.type === 'income';
            return (
              <li key={tx.id} className="list-item">
                <div className="list-item-left">
                  <span className="cat-icon">{getCategoryIcon(tx.categoryId)}</span>
                  <div>
                    <div className="list-item-title">{getCategoryName(tx.categoryId)}</div>
                    {tx.description && <div className="list-item-sub">{tx.description}</div>}
                  </div>
                </div>
                <span className={`amount ${isIncome ? 'amount-income' : 'amount-expense'}`}>
                  {isIncome ? '+' : '−'}{formatMoneyPrecise(tx.amount)}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {confirmPlanned && (
        <Modal title="Провести операцию" onClose={() => setConfirmPlanned(null)}>
          <p style={{ color: 'var(--text-dim)', marginTop: 0 }}>
            Запланировано: {scheduledTitle(confirmPlanned)} · {formatMoneyPrecise(confirmPlanned.amount)}
          </p>
          <TransactionForm
            categories={categories}
            periodId={activePeriod?.id}
            transaction={plannedTx}
            onSave={handleConfirmPlanned}
            onCancel={() => setConfirmPlanned(null)}
          />
        </Modal>
      )}
    </div>
  );
}
